import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, SendIcon } from 'lucide-react';
import { useWorkspace } from '../../store/workspaceStore';
import { Proposal } from '../../types';
export function NewRequestModal({
  onClose,
  onCreated
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const people = useWorkspace(s => s.people);
  const createProposal = useWorkspace(s => s.createProposal);
  const brokers = people.filter(p => p.role === 'broker');
  const [property, setProperty] = useState('');
  const [address, setAddress] = useState('');
  const [assetType, setAssetType] = useState('Office');
  const [deckType, setDeckType] = useState<Proposal['deckType']>('Proposal');
  const [requestedById, setRequestedById] = useState(brokers[0]?.id ?? '');
  const canSubmit = property.trim() && address.trim() && requestedById;
  const submit = () => {
    if (!canSubmit) return;
    const id = createProposal({
      property: property.trim(),
      address: address.trim(),
      assetType,
      deckType,
      requestedById
    });
    onCreated(id);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose} data-id="element-124" data-mp="2cn8az1m1wkd9">
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      duration: 0.18
    }} className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-pop" onClick={e => e.stopPropagation()} data-id="element-125" data-mp="1yoiard3xq4ko">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4" data-id="element-126" data-mp="2wbz76ugi08v8">
          <div data-id="element-127" data-mp="2hn30b0q6h1w7">
            <h2 className="text-base font-bold text-slate-900" data-id="element-128" data-mp="2hljk1a4iuquf">Request a Proposal / BOV / Pitch Deck</h2>
            <p className="text-xs text-slate-500" data-id="element-129" data-mp="2mkcxj2jt6hul">Notifies Faraz, Michael & Simon + the requesting broker.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-id="element-130" data-mp="3a9xhnorp7l5g">
            <XIcon className="h-5 w-5" data-id="element-131" data-mp="3f39w562nybkh" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5" data-id="element-132" data-mp="3owmfzna7rsa9">
          <Field label="Property name" data-id="element-133" data-mp="3jyuogudhe86l">
            <input value={property} onChange={e => setProperty(e.target.value)} placeholder="e.g. Harborview Tower" className={inputCls} autoFocus data-id="element-134" data-mp="3jzvziyuhyuob" />
          </Field>
          <Field label="Address" data-id="element-135" data-mp="3ayg01m580cyv">
            <input value={address} onChange={e => setAddress(e.target.value)} placeholder="e.g. 1200 Marina Blvd, San Diego, CA" className={inputCls} data-id="element-136" data-mp="3ax4o2z1mfywo" />
          </Field>

          <div className="grid grid-cols-2 gap-4" data-id="element-137" data-mp="317ut9bfe824v">
            <Field label="Deck type" data-id="element-138" data-mp="3171w9268kqzx">
              <select value={deckType} onChange={e => setDeckType(e.target.value as Proposal['deckType'])} className={inputCls} data-id="element-139" data-mp="2w90aualc8fla">
                <option data-id="element-140" data-mp="2rb88snqzbd3d">Proposal</option>
                <option data-id="element-141" data-mp="2r840pfae7x7f">BOV</option>
                <option data-id="element-142" data-mp="2n93h3uk3cqjd">Pitch Deck</option>
              </select>
            </Field>
            <Field label="Asset type" data-id="element-143" data-mp="2i9qvte8vbriw">
              <select value={assetType} onChange={e => setAssetType(e.target.value)} className={inputCls} data-id="element-144" data-mp="2iatmpm66xtyt">
                {['Office', 'Industrial', 'Retail', 'Multifamily', 'Self Storage', 'Medical Office', 'Land'].map(a => <option key={a} data-id="element-145" data-mp="12snktltfh2oe">{a}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Requesting broker" data-id="element-146" data-mp="1br93dgywghp6">
            <select value={requestedById} onChange={e => setRequestedById(e.target.value)} className={inputCls} data-id="element-147" data-mp="1bpxz7g1jl7xj">
              {brokers.map(b => <option key={b.id} value={b.id} data-id="element-148" data-mp="j2agy7nqute2">{b.name}</option>)}
            </select>
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-6 py-4" data-id="element-149" data-mp="o0lqeha4n1vr">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100" data-id="element-150" data-mp="st52tax2vqyz">
            Cancel
          </button>
          <button onClick={submit} disabled={!canSubmit} className="inline-flex items-center gap-2 rounded-lg bg-brand-accent px-4 py-2 text-sm font-semibold text-white hover:bg-brand disabled:cursor-not-allowed disabled:opacity-40" data-id="element-151" data-mp="bsnu1z01vs6">
            <SendIcon className="h-4 w-4" data-id="element-152" data-mp="2br1cetsgmdra" /> Send Request
          </button>
        </div>
      </motion.div>
    </div>;
}
const inputCls = 'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-accent';
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <label className="block" data-id="element-153" data-mp="2gp2vvftssf3a">
      <span className="mb-1 block text-xs font-semibold text-slate-600" data-id="element-154" data-mp="2gpw7au3pbsrr">{label}</span>
      {children}
    </label>;
}